/**
 * FAQ PDF Export Utility
 * Generates a PDF from the currently visible FAQ section.
 */

import html2canvas from "html2canvas";
import type { FAQItem, FAQSection } from "./faqData";

export const exportFAQToPDF = async (
	currentSection: FAQSection,
	filteredFAQs: FAQItem[],
): Promise<void> => {
	// Create a temporary container for PDF content
	const pdfContainer = document.createElement("div");
	pdfContainer.style.position = "absolute";
	pdfContainer.style.left = "-9999px";
	pdfContainer.style.width = "800px";
	pdfContainer.style.background = "white";
	pdfContainer.style.padding = "40px";
	pdfContainer.style.fontFamily = "Arial, sans-serif";

	// Add header
	let html = `
    <div style="border-bottom: 3px solid #1967d2; padding-bottom: 20px; margin-bottom: 30px;">
      <h1 style="font-size: 28px; color: #1a1a2e; margin: 0 0 10px 0; font-weight: 700;">${currentSection.title}</h1>
      <p style="color: #666; font-size: 16px; margin: 0 0 8px 0;">${currentSection.description || ""}</p>
      <p style="color: #999; font-size: 12px; margin: 10px 0 0 0;">
        Generated: ${new Date().toLocaleDateString()} | Total Questions: ${filteredFAQs.length}
      </p>
    </div>
  `;

	// Add FAQ items
	filteredFAQs.forEach((faq, idx) => {
		html += `
      <div style="margin-bottom: 30px; border-left: 4px solid #1967d2; padding-left: 20px;">
        <div style="display: flex; gap: 12px; margin-bottom: 12px;">
          <div style="display: flex; align-items: center; justify-content: center; min-width: 30px; height: 30px; background: #1967d2; color: white; border-radius: 50%; font-weight: 700; font-size: 14px;">
            ${idx + 1}
          </div>
          <div style="font-size: 16px; font-weight: 700; color: #1a1a2e;">${faq.question}</div>
        </div>
        <p style="color: #555; line-height: 1.8; font-size: 14px; margin-bottom: 12px;">${faq.answer}</p>
    `;

		if (faq.tags && faq.tags.length > 0) {
			html += `
        <div style="display: flex; gap: 8px; flex-wrap: wrap; margin-top: 12px;">
          ${faq.tags.map((tag) => `<span style="display: inline-block; background: #e3f2fd; color: #1967d2; padding: 4px 10px; border-radius: 16px; font-size: 12px; font-weight: 500;">${tag}</span>`).join("")}
        </div>
      `;
		}

		html += `</div>`;
	});

	// Add footer
	html += `
    <div style="margin-top: 40px; padding-top: 20px; border-top: 1px solid #e0e0e0; font-size: 12px; color: #999; text-align: center;">
      <p style="margin: 0;">This document contains FAQ answers related to AUSTRAC Regulatory Compliance 2026</p>
      <p style="margin: 5px 0 0 0;">For official information, visit www.austrac.gov.au</p>
    </div>
  `;

	pdfContainer.innerHTML = html;
	document.body.appendChild(pdfContainer);

	try {
		// Convert to canvas
		const canvas = await html2canvas(pdfContainer, {
			scale: 2,
			backgroundColor: "#ffffff",
		});

		const { jsPDF } = await import("jspdf");
		const pdf = new jsPDF({
			orientation: "portrait",
			unit: "mm",
			format: "a4",
		});

		const imgData = canvas.toDataURL("image/png");
		const pageWidth = pdf.internal.pageSize.getWidth();
		const pageHeight = pdf.internal.pageSize.getHeight();
		const imgWidth = pageWidth - 20; // 10mm margins
		const imgHeight = (canvas.height * imgWidth) / canvas.width;

		let heightLeft = imgHeight;
		let position = 10; // 10mm top margin

		// Add first page
		pdf.addImage(imgData, "PNG", 10, position, imgWidth, imgHeight);
		heightLeft -= pageHeight - 20;

		// Add subsequent pages if needed
		while (heightLeft > 0) {
			position = heightLeft - imgHeight + 10;
			pdf.addPage();
			pdf.addImage(imgData, "PNG", 10, position, imgWidth, imgHeight);
			heightLeft -= pageHeight - 20;
		}

		// Download PDF
		pdf.save(
			`FAQ-${currentSection.id}-${new Date().toISOString().split("T")[0]}.pdf`,
		);
	} finally {
		// Cleanup
		document.body.removeChild(pdfContainer);
	}
};
